import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { get as _get, has as _has } from 'lodash';
import { map } from 'rxjs/operators';

import { SearchContext } from '../+state/service-locator/service-locator.models';
import { GeoCoderService } from './geo-coder.service';

// tag added from this angular issue: https://github.com/angular/angular/issues/19698#issuecomment-338340211
// @dynamic
@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  constructor(private geoCoder: GeoCoderService) {}

  static getValidatorErrorMessage(validatorName: string, validatorValue?: any, controlValue?: any) {
    // TODO: Verify if we need to add more
    const config = {
      required: 'Required',
      requiredWithTrim: 'Your answer cannot be only spaces',
      noErrorMessage: ' ',
      minlength: `Minimum length 5`,
      maxlength: `Maximum length 5`,
      invalidAndOr: controlValue + ' is not a valid entry.',
      invalidZipcode: 'Zipcode is invalid',
      invalidExpirationYear: 'Invalid expiration year',
      zipCodeNotFive: 'Minimum Length 5',
      onlyNumbers: 'Can have only numbers',
      minShopNameLength: `Shop Name minimum length is 1`,
      maxShopNameLength: `Shop Name maximum length is 50`,
      minShopCityLength: `Shop City minimum length is 1`,
      maxShopCityLength: `Shop City maximum length is 50`,
      invalidCity: `Invalid city.`,
      nonAsciiCharacters: `Only letters, numbers, spaces, and punctuation are allowed.`,
      nonExistentZipcode: 'Zipcode does not exist',
      zipcodeNotRecognized: 'Zipcode is not recognized'
    };

    return config[validatorName];
  }

  /* ================================================================================
      01 -- Base validations (e.g., required, length)
  */

  static isChecked(control: UntypedFormControl) {
    if (control.value) {
      return null;
    }

    return { required: true };
  }
  static requiresOneOrMore(group: UntypedFormGroup) {
    for (const name in group.controls) {
      if (name) {
        // fixing typescript if filter error
        const control = group.controls[name];
        if (control.value) {
          return null;
        }
      }
    }

    return { selectOneOrMore: true };
  }

  static requiredWhen(conditions: any) {
    return (control: UntypedFormControl): { [s: string]: boolean } => {
      let required = false;
      if (conditions()) {
        required = true;
      }

      if (required && !control.value && control.value !== false) {
        return { required: true };
      }

      return null;
    };
  }

  // ng2 default required validator doesn't trim strings before checking
  // by trimming we enforce that a required field cannot contain only spaces
  static requiredWithTrim(control: UntypedFormControl) {
    return typeof control.value === 'string' && !control.value.trim()
      ? { requiredWithTrim: true }
      : null;
  }

  // usage validateWhen(this === true, Validators.compose([...]))
  static validateWhen(preConditions: any, validator: ValidatorFn) {
    return (control: UntypedFormControl): { [s: string]: boolean } => {
      const required = false;
      if (preConditions()) {
        return validator(control);
      }

      return null;
    };
  }

  static emptyOrLettersOnly(control: UntypedFormControl) {
    if (!control.value.match(/[a-zA-Z]*/)) {
      return { emptyOrLettersOnly: true };
    }

    return null;
  }

  //// Numeric

  static atLeastOneNumber(control: UntypedFormControl) {
    if (!control.value.match(/^(?=.*[0-9])/)) {
      return { atLeastOneNumberError: true };
    }

    return null;
  }

  static numbersOnly(control: UntypedFormControl) {
    if (!control.value.match(/^[0-9]*$/)) {
      return { noErrorMessage: true };
    }

    return null;
  }

  static invalidChar(control: UntypedFormControl) {
    const invalidChars = [63];
    if (control.value) {
      for (const sub of control.value) {
        if (invalidChars.includes(sub.charCodeAt(0))) {
          return { invalidCharacter: true };
        }
      }
    }
    return null;
  }

  static nonAsciiCharacters(control: UntypedFormControl) {
    if (control.value && control.value.match(/[^\x00-\x7F]/g)) {
      // for char hex codes see: http://defindit.com/ascii.html
      // this is the same as allowing charCode 0-127 or only keyboard input
      return { nonAsciiCharacters: true };
    }

    return null;
  }

  /*
   *Specific validator for the zip code for
   *enrollment.  Needs to be numbers and 5 digits
   */
  static zipCodeNumbersAndLength(control: UntypedFormControl) {
    if (control.value) {
      if (
        !control.value.match(/^[0-9]+$/) ||
        control.value.length < 5 ||
        control.value.length > 5
      ) {
        return { invalidZipcode: true };
      }
    }

    return null;
  }

  static zipCodeValidator(control: UntypedFormControl) {
    if (control.value != null && control.value.match(/^\d{5}(-\d{4})?$/)) {
      return null;
    }

    return { invalidZipcode: true };
  }

  static shopNameGenericValidation(control: UntypedFormControl) {
    if (control.value) {
      if (control.value.length < 1) {
        return { minShopNameLength: true };
      } else if (control.value.length > 50) {
        return { maxShopNameLength: true };
      }
    }

    return null;
  }

  static shopNameCityValidation(control: UntypedFormControl) {
    if (control.value) {
      if (control.value.length < 1) {
        return { minShopCityLength: true };
      } else if (control.value.length > 50) {
        return { maxShopCityLength: true };
      }
    }

    return null;
  }

  zipExists(zip: string, context: SearchContext) {
    return this.geoCoder.parse(zip, context).pipe(map(res => res.addressMatchDetail));
  }
}
